import apiClient from "@/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { campaignRequestErrorMessage, campaignRequestsKeys } from "./use-campaign-requests";

export interface PreparationConfig {
	provingPersonaId?: string;
	provingScenarioIds?: string[];
	provingMetricIds?: string[];
	agentTemplateId: string;
	campaignTemplateId: string;
	leadServiceId: string;
	agentServiceId: string;
	postCallServiceId?: string;
}
export interface PreparationRun {
	runId: string;
	status: "queued" | "running" | "review" | "approved" | "failed";
	error?: string;
	accepted?: number;
	rejected?: number;
	approvedAt?: string;
	approvedBy?: string;
	rejections?: { file: string; row: number; reason: string }[];
	models?: Record<string, { serviceId: string; model: string | null; version: number; scope: string }>;
	proposedOutcomes?: { name: string; description: string; isConversion: boolean }[];
}
export interface PreparationView {
	proving: {
		status: "not_started" | "queued" | "running" | "passed" | "failed" | "stale";
		passed: number;
		total: number;
		error: string | null;
		runs: {
			id: number;
			agentId: string;
			status: string;
			completed: number;
			total: number;
			passed: boolean;
			suggestions: { title: string; problem: string; after: string }[];
			suggestionsAt: string | null;
			scenarios: {
				id: number;
				name: string;
				status: string;
				completionIssue: string | null;
				passed: boolean;
				checks: { name: string; passed: boolean | null; reason: string | null; notApplicable: boolean }[];
			}[];
		}[];
	};
	groups: {
		market: string;
		groupId: string;
		count: number;
		sample: { phone: string; attributes: Record<string, string> }[];
	}[];
	run: PreparationRun | null;
	agents: {
		id: string;
		name: string;
		prompt: string;
		firstPhrase: string;
		postCallPrompt: string | null;
		conversionNotes: string[];
	}[];
}
export interface PreparationOptions {
	proving: {
		personas: { id: string; name: string }[];
		scenarios: { id: string; name: string }[];
		metrics: { id: string; name: string }[];
	};
	account: { id: string; name: string };
	config: PreparationConfig | null;
	agents: { id: string; name: string }[];
	campaigns: { id: string; title: string }[];
	services: { id: string; title: string; model: string; provider: string }[];
	groups: { id: string; name: string; isStatic: boolean }[];
	numbers: { id: number; number: string; countryCode: string | null }[];
}
export function useCampaignPreparation(id: string) {
	return useQuery({
		queryKey: ["campaign-preparation", id],
		queryFn: async (): Promise<PreparationView> => {
			const response = await apiClient.get(`/campaign-requests/${id}/preparation`);
			return response.data;
		},
		refetchInterval: (query) =>
			["queued", "running"].includes(query.state.data?.run?.status ?? "") ||
			["queued", "running"].includes(query.state.data?.proving?.status ?? "") ||
			query.state.data?.proving?.runs.some((run) => run.status === "completed" && !run.suggestionsAt)
				? 3000
				: false,
	});
}
export function usePreparationOptions(id: string) {
	return useQuery({
		queryKey: ["campaign-preparation-options", id],
		enabled: !!id,
		queryFn: async (): Promise<PreparationOptions> => {
			const response = await apiClient.get(`/campaign-requests/${id}/preparation/options`);
			return response.data;
		},
	});
}
export function usePreparationAction(id: string) {
	const client = useQueryClient();
	return useMutation({
		mutationFn: async ({
			action,
			data,
		}: {
			action: "settings" | "retry" | "approve" | "agent" | "post-call" | "proving-retry";
			data?: PreparationConfig | { agentId: string; prompt: string; firstPhrase?: string };
		}) => {
			const method = action === "settings" || action === "agent" || action === "post-call" ? "put" : "post";
			const response = await apiClient[method](`/campaign-requests/${id}/preparation/${action}`, data ?? {});
			return response.data;
		},
		onSuccess: async (_, { action }) => {
			await Promise.all([
				client.invalidateQueries({ queryKey: ["campaign-preparation", id] }),
				client.invalidateQueries({ queryKey: ["campaign-preparation-options", id] }),
				client.invalidateQueries({ queryKey: campaignRequestsKeys.detail(id) }),
				client.invalidateQueries({ queryKey: campaignRequestsKeys.adminQueues() }),
			]);
			toast.success(
				{
					settings: "Account preparation settings saved",
					retry: "Preparation queued",
					approve: "Approved; Proving Ground tests requested",
					"proving-retry": "Proving Ground tests requested",
					agent: "Draft saved; review it before approval",
					"post-call": "Post-call prompt saved; review it before approval",
				}[action]
			);
		},
		onError: (error) => toast.error(campaignRequestErrorMessage(error, "Could not update preparation")),
	});
}
